/** @jsx isolateComponent */
import { isolateComponent } from '@zapier/style-encapsulation';

import { Icon, IconName } from '../../display/Icon';
import { TooltipPosition } from '../../display/Tooltip';
import { TooltipWrapper } from '../../display/TooltipWrapper';
import {
  BaseButton,
  Props as BaseButtonProps,
} from '../../navigation/Button/BaseButton';

export type Props = Omit<
  BaseButtonProps,
  'children' | 'iconBefore' | 'isFullWidth' | 'isPill' | 'isSquare'
> & {
  color?:
    | 'icon-primary'
    | 'icon-primary-dark'
    | 'icon-alt'
    | 'icon-secondary'
    | 'icon-secondary-dark';
  /**
   * Name of the `Icon` to render.
   */
  icon: IconName;
  /**
   * Size of the `Icon` that renders within the `Button`.
   * Typically unnecessary since the `size` prop will map
   * the desired `Icon` size in most circumstances.
   */
  iconSize?: number;
  /**
   * Indicates what the button does. Used for the `ariaLabel` on the
   * `Button` as well as the text for the `Tooltip`.
   * This prop should be supplied in most circumstances, but in cases
   * **when it's not desired be sure to pass `ariaLabel`!**
   */
  label?: string;
  /** Indicates whether to flip the `Tooltip` based on available space. */
  shouldCorrectTooltipPosition?: boolean;
  /**
   * Optional prop to associate the tooltip and the button that controls it.
   */
  tooltipId?: string;
  /**
   * Where to render the `Tooltip` relative to the button.
   */
  tooltipPosition?: TooltipPosition;
  /**
   * Renders `IconButton` in a circle shape.
   */
  isCircle?: boolean;
};

/**
 * One IconButton to rule them all
 * Used by IconButton and DarkBackgroundIconButton
 */

export function BaseIconButton({
  icon,
  iconSize,
  isCircle,
  label,
  shouldCorrectTooltipPosition,
  tooltipId,
  tooltipPosition,
  ...buttonProps
}: Props) {
  return (
    <TooltipWrapper
      content={label}
      position={tooltipPosition}
      shouldCorrectPosition={shouldCorrectTooltipPosition}
      showTooltipOnTouchDevices={false}
      tooltipId={tooltipId}
    >
      {({ childProps }) => (
        <BaseButton
          ariaLabel={label}
          {...buttonProps}
          {...childProps}
          isPill={isCircle}
          isSquare={true}
        >
          <Icon
            canAcceptPointerEvents={false}
            isBlock={true}
            name={icon}
            size={24}
          />
        </BaseButton>
      )}
    </TooltipWrapper>
  );
}
